import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from "recharts";
import { ChartLineUp, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const COLORS = ["#3EB6F0", "#F59E0B", "#10B981"];

export default function MultiMeterChart({ utenze }) {
  const [selected, setSelected] = useState([]);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = (id) => {
    if (selected.includes(id)) setSelected(selected.filter((s) => s !== id));
    else if (selected.length < 3) setSelected([...selected, id]);
  };

  useEffect(() => {
    if (selected.length === 0) { setSeries(null); return; }
    setLoading(true);
    api.get(`/v1/telemetry-compare?meter_ids=${selected.join(",")}&hours=24`)
      .then((r) => setSeries(r.data.series))
      .catch(() => setSeries(null))
      .finally(() => setLoading(false));
  }, [selected]);

  // align series by timestamp index (all series get index-based x)
  const chartData = React.useMemo(() => {
    if (!series) return [];
    const maxLen = Math.max(0, ...Object.values(series).map((s) => s.length));
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      const row = { i };
      for (const [id, arr] of Object.entries(series)) {
        if (arr[i]) {
          row[id] = arr[i].flow_lh;
          row.ts = arr[i].timestamp;
        }
      }
      rows.push(row);
    }
    return rows;
  }, [series]);

  return (
    <div className="bg-[#111827] border border-slate-800 rounded-lg" data-testid="multi-meter-chart">
      <div className="px-5 py-4 border-b border-slate-800 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <ChartLineUp size={20} weight="duotone" className="text-[#3EB6F0]" />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Grafico Comparativo</div>
            <div className="font-heading text-lg text-white">Storico 24h Multi-Meter</div>
          </div>
        </div>
        <div className="text-[11px] font-mono text-slate-500 max-w-md">
          Seleziona fino a <span className="text-[#3EB6F0]">3 allacci</span> per confrontare l'andamento e individuare correlazioni di perdita.
        </div>
      </div>

      <div className="px-5 py-3 border-b border-slate-800 flex gap-2 flex-wrap">
        {utenze.map((u) => {
          const idx = selected.indexOf(u.id);
          const active = idx >= 0;
          const color = active ? COLORS[idx] : "#334155";
          return (
            <button
              key={u.id}
              onClick={() => toggle(u.id)}
              disabled={!active && selected.length >= 3}
              data-testid={`compare-toggle-${u.id}`}
              className="px-3 py-1 rounded-md text-xs font-mono border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                borderColor: active ? color : "#1E293B",
                background: active ? color + "22" : "transparent",
                color: active ? color : "#94A3B8",
              }}
            >
              {active && <span className="mr-1">●</span>}
              {u.id}
            </button>
          );
        })}
        {selected.length > 0 && (
          <button onClick={() => setSelected([])} className="text-xs text-slate-500 hover:text-slate-300 gap-1 inline-flex items-center px-2" data-testid="compare-clear">
            <X size={12} /> Reset
          </button>
        )}
      </div>

      <div className="p-4 h-[280px]">
        {selected.length === 0 ? (
          <div className="h-full grid place-items-center text-xs font-mono text-slate-500 text-center">
            Nessun allaccio selezionato. Scegli fino a 3 contatori dalle pillole qui sopra.
          </div>
        ) : loading ? (
          <div className="h-full grid place-items-center text-xs font-mono text-slate-500">Caricamento serie...</div>
        ) : chartData.length < 2 ? (
          <div className="h-full grid place-items-center text-xs font-mono text-slate-500">Dati insufficienti per il grafico.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis dataKey="i" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickLine={{ stroke: "#334155" }} axisLine={{ stroke: "#334155" }} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickLine={{ stroke: "#334155" }} axisLine={{ stroke: "#334155" }} label={{ value: "L/h", angle: -90, position: "insideLeft", fill: "#64748B", fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: "#0a0f1c", border: "1px solid #1E293B", fontSize: 12, fontFamily: "IBM Plex Mono" }}
                labelFormatter={(v, p) => `Punto #${v}${p?.[0]?.payload?.ts ? " · " + new Date(p[0].payload.ts).toLocaleString("it-IT") : ""}`}
              />
              <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, color: "#94A3B8" }} />
              {selected.map((id, idx) => (
                <Line key={id} type="monotone" dataKey={id} stroke={COLORS[idx]} strokeWidth={2} dot={false} name={id} isAnimationActive={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
